import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Briefcase, 
  Calendar, 
  Settings, 
  Plus,
  Users,
  FileText,
  Menu,
  X,
  Phone,
  HelpCircle,
  HardDrive,
  BookOpen
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { QuoVadisLogo } from '../ui/QuoVadisLogo';

interface SidebarProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}

const navigationItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Candidature', href: '/applications', icon: Briefcase },
  { name: 'Colloqui', href: '/calendar', icon: Calendar },
  { name: 'Documenti', href: '/documents', icon: FileText },
  { name: 'Drive', href: '/drive', icon: HardDrive },
  { name: 'Come Funziona?', href: '/help', icon: BookOpen },
  { name: 'FAQ', href: '/faq', icon: HelpCircle },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
];

const moderatorItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Gestione Candidature', href: '/moderator/applications', icon: Users },
  { name: 'I Miei Contatti', href: '/moderator/contacts', icon: Phone },
  { name: 'Gestione Interviste', href: '/moderator/interviews', icon: Calendar },
  { name: 'Documenti', href: '/documents', icon: FileText },
  { name: 'Drive', href: '/drive', icon: HardDrive },
  { name: 'Come Funziona?', href: '/help', icon: BookOpen },
  { name: 'FAQ', href: '/faq', icon: HelpCircle },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
];

const adminItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Gestione Utenti', href: '/admin/users', icon: Users },
  { name: 'Gestione FAQ', href: '/admin/faq', icon: HelpCircle },
  { name: 'Drive', href: '/drive', icon: HardDrive },
  { name: 'Come Funziona?', href: '/help', icon: BookOpen },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
];

export const Sidebar: React.FC<SidebarProps> = ({ currentPage = 'dashboard', onNavigate }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isSidebarOpen, closeSidebar } = useSidebar();
  const navigate = useNavigate();
  
  console.log('🔄 Sidebar renderizzata con currentPage:', currentPage);
  
  const handleNavigation = (href: string) => {
    console.log('🔄 Sidebar handleNavigation:', href);
    // Usa solo React Router per la navigazione
    navigate(href);
    setMobileOpen(false); // chiudi sidebar su mobile dopo click
  };

  // Prendo il profilo utente per mostrare le voci appropriate in base al ruolo
  const { userProfile } = useAuthStore();
  const isAdmin = userProfile?.role === 'admin';
  const isModerator = userProfile?.role === 'moderator';

  // Debug quando il profilo cambia
  useEffect(() => {
    console.log('🔄 Sidebar: Profilo utente cambiato:', {
      userProfile,
      role: userProfile?.role,
      isAdmin,
      isModerator
    });
  }, [userProfile, isAdmin, isModerator]);

  // Seleziona gli elementi di navigazione appropriati
  const items = isAdmin ? adminItems : isModerator ? moderatorItems : navigationItems;

  // Debug per verificare il ruolo
  console.log('🔍 Sidebar Debug:', {
    userProfile,
    role: userProfile?.role,
    isAdmin,
    isModerator,
    itemsCount: isAdmin ? adminItems.length : isModerator ? moderatorItems.length : navigationItems.length,
    moderatorItems: moderatorItems.map(item => item.name),
    currentPage,
    items: items.map(item => item.name)
  });

  // Hamburger visibile solo su mobile, a destra
  return (
    <>
      {/* Hamburger menu (mobile only, right) */}
      <button
        className="fixed top-4 right-4 z-40 bg-white rounded-md shadow-md p-2 sm:hidden focus:outline-none"
        onClick={() => setMobileOpen(true)}
        aria-label="Apri menu"
      >
        <Menu className="h-6 w-6 text-blue-600" />
      </button>
      {/* Overlay mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 sm:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {/* Sidebar (da destra su mobile, da sinistra su desktop) */}
      <div
        className={
          `
          fixed top-0 right-0 h-full z-40 bg-white shadow-sm border-l border-gray-200 min-h-screen w-64
          transform transition-transform duration-300 ease-in-out
          overflow-y-auto
          ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}
          sm:left-0 sm:right-auto sm:border-r sm:border-l-0 sm:translate-x-0 sm:flex sm:flex-col
          ${isSidebarOpen ? 'sm:translate-x-0' : 'sm:-translate-x-full'}
          `
        }
        style={{ willChange: 'transform' }}
      >
        {/* Close button mobile */}
        <button
          className="absolute top-4 left-4 z-50 bg-white rounded-md p-1 shadow-md sm:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Chiudi menu"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>
        
        {/* Logo and Brand - Solo su desktop */}
        <div className="hidden sm:flex flex-col items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <QuoVadisLogo size="md" className="mb-2" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white text-center">QUO VADIS?</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">Traccia le tue Candidature</p>
        </div>

        {/* Quick Actions */}
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 sm:pt-4 pt-16">
          <button
            onClick={() => handleNavigation('/applications/new')}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Candidatura
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-2">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.href.replace(/^\//, '') || 
                            (currentPage === 'applications' && item.href === '/applications') ||
                            (currentPage === 'dashboard' && item.href === '/dashboard') ||
                            (currentPage === 'calendar' && item.href === '/calendar') ||
                            (currentPage === 'settings' && item.href === '/settings') ||
                            (currentPage === 'moderator-applications' && item.href === '/moderator/applications') ||
                            (currentPage === 'moderator-contacts' && item.href === '/moderator/contacts') ||
                            (currentPage === 'moderator-interviews' && item.href === '/moderator/interviews');

            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className="mr-3 h-4 w-4" />
                {item.name}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}; 